import { antdMessage } from '@hellodarwin/core/lib/features/providers/antd-app-provider';
import { usePremiumAccess } from '@hellodarwin/app-core/lib/providers/premium-access-provider';
import { FormFillingProps } from '@hellodarwin/app-features/lib/features/gin-steps/gin-steps-provider';
import { ApplicationItemState } from '@hellodarwin/app-features/lib/features/gin-steps/types';
import {
  GinStepApplicationItem,
  InitialGinStepApplicationItem,
} from '@hellodarwin/core/lib/features/entities/gin-step-entities';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../app/app-hooks';
import {
  deleteGinStepFormApplicationItem,
  fetchGinStepForm,
  selectGinStepForm,
  selectItemsById,
  updateGinStepProperty,
  validateGinStep,
} from '../../../../../features/api/slices/gin-steps-slice';
import {
  fetchProvinces,
  selectProvinces,
} from '../../../../../features/api/slices/grants-slice';
import {
  fetchBottomLevelIndustries,
  fetchIndustries,
  selectIndustriesSectors,
  selectIndustriesSubsectors,
  selectNaicsCodes,
} from '../../../../../features/api/slices/tags-slice';
import {
  useClientApi,
  useNewClientApi,
} from '../../../../../features/api/use-client-api';
import useGinStepDocumentSubmission from '../../../../../hooks/use-gin-step-document-submission';

interface UseGinStepUtilsProps {
  programId: string;
}

const useGinStepUtils = ({ programId }: UseGinStepUtilsProps) => {
  const dispatch = useAppDispatch();
  const oldApi = useClientApi();
  const { selectedLocale } = useLocale();
  const api = useNewClientApi();
  const { t } = useTranslations();
  const ginForm = useAppSelector(selectGinStepForm);
  const itemsById = useAppSelector(selectItemsById);
  const { accessLevel } = usePremiumAccess();
  const loadingItems = useAppSelector((state) => state.ginSteps.loadingItems);
  const {
    assetApplicationProps,
    handleSubmitApplicationItem,
    triggerProgramStatusDialog,
    handleDownloadAsset,
    applicationStatusModal,
    closeApplicationStatusModal,
  } = useGinStepDocumentSubmission({ programId });

  const industrySectors = useAppSelector(selectIndustriesSectors);
  const industrySubsectors = useAppSelector(selectIndustriesSubsectors);
  const naicsCodes = useAppSelector(selectNaicsCodes);
  const provinces = useAppSelector(selectProvinces);

  useEffect(() => {
    if (programId) {
      dispatch(
        fetchGinStepForm({
          api,
          programId,
          ownerType: accessLevel === 'custom' ? 'Client' : '',
        }),
      );
    }
  }, [dispatch, api, programId, accessLevel]);

  useEffect(() => {
    if (industrySectors.length === 0) {
      dispatch(fetchIndustries({ api: oldApi, locale: selectedLocale }));
    }
  }, [dispatch, oldApi, selectedLocale, industrySectors.length]);

  useEffect(() => {
    if (provinces.length === 0) {
      dispatch(fetchProvinces({ api: oldApi, locale: selectedLocale }));
    }
  }, [dispatch, oldApi, selectedLocale, provinces.length]);

  useEffect(() => {
    if (naicsCodes.length === 0) {
      dispatch(
        fetchBottomLevelIndustries({
          api,
          locale: selectedLocale,
        }),
      );
    }
  }, [dispatch, api, selectedLocale, naicsCodes.length]);

  const applicationItemStatesById:
    | Map<string, ApplicationItemState>
    | undefined = useMemo(() => {
    const applicationItemStatesById = new Map<string, ApplicationItemState>();
    if (!ginForm?.form_application_id || !itemsById) return undefined;
    const items = Object.values(itemsById);
    items.forEach((item) => {
      const appItem = item.application_item;

      applicationItemStatesById.set(item.item_id, {
        isLoading: Boolean(loadingItems[item.item_id]),
        ginStepItemApplication: appItem,
        formApplicationId: ginForm?.form_application_id ?? '',
        meetingState: undefined,
      });
    });
    return applicationItemStatesById;
  }, [ginForm?.form_application_id, loadingItems, itemsById]);

  const handleDeleteApplicationItem = async (item: GinStepApplicationItem) => {
    try {
      await dispatch(
        deleteGinStepFormApplicationItem({
          api,
          ginStepApplicationItem: item,
          programId,
        }),
      ).unwrap();
      antdMessage.success(t('gin_steps|item_delete_successful'));
    } catch {
      antdMessage.error(t('gin_steps|item_delete_error'));
    }
  };

  const propertyItemProps: FormFillingProps['propertyItemProps'] | undefined =
    useMemo(() => {
      return {
        industrySectors,
        industrySubsectors: industrySubsectors.map((industry) => ({
          value: industry.id,
          label: industry.class_title ?? '',
        })),
        naicsCodes,
        provinces,
        updateGinStepProperty: async (value, ginStepItem, appItem) => {
          const response = await dispatch(
            updateGinStepProperty({
              api,
              value,
              ginStepItem,
              appItem: appItem || InitialGinStepApplicationItem,
            }),
          ).unwrap();
          return response.form;
        },
      } satisfies FormFillingProps['propertyItemProps'];
    }, [
      dispatch,
      api,
      industrySectors,
      industrySubsectors,
      naicsCodes,
      provinces,
    ]);

  const handleUpdateStepValidation = async (ginStepId: string) => {
    try {
      const updatedForm = await dispatch(
        validateGinStep({
          api: oldApi,
          programId,
          ginStepId,
        }),
      ).unwrap();

      antdMessage.success('Step validated successfully');

      if (updatedForm.program_status_trigger) {
        triggerProgramStatusDialog(updatedForm.program_status_trigger);
      }

      return updatedForm;
    } catch (e: any) {
      antdMessage.error('Failed to validate step');
      return Promise.reject(e);
    }
  };

  return {
    assetApplicationProps,
    propertyItemProps,
    handleSubmitApplicationItem,
    handleDeleteApplicationItem,
    applicationItemStatesById,
    applicationStatusModal,
    closeApplicationStatusModal,
    triggerProgramStatusDialog,
    handleDownloadAsset,
    handleUpdateStepValidation,
  };
};

export default useGinStepUtils;
